import { AxiosRequestConfig } from "axios";
import BuyoutRecord from "integration/records/buyout-record";
import { Signal } from "signals";
import BetOptions from "slotworks/components/betbar/vo/bet-options";
import Components from "slotworks/components/components";
import ButtonElement, { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import currency from "slotworks/i18n/currency";
import revolverModel from "slotworks/integration/revolver/model/revolver-model";
import betModel from "slotworks/model/bets/bet-model";
import gameState from "slotworks/model/game-state";
import slotDefinition from "slotworks/model/slot-definition";
import commsManager from "slotworks/server/comms-manager";
import smartButton, { SmartButtonFlag } from "slotworks/smart-button/smart-button";
import stateMachine from "slotworks/state-machine/state-machine";
import Contract from "slotworks/utils/contract";
import deviceInfo from "slotworks/utils/device-info";
import { sequence } from "slotworks/utils/race-utils";
import HarleyComponents from "components/components";

export default class BonusBuyComponent {

    public static MULTIPLIER: number = 50;

    private readonly scene = "bonusbuy";

    public readonly onHide = new Signal();

    private layer: Layers;
    private inResolve: Function;

    private betOptions: BetOptions;

    private closeButton: ButtonElement;
    private buyButton: ButtonElement;
    private plusButton: ButtonElement;
    private minusButton: ButtonElement;

    private totalBetText: PIXI.Text; // bet
    private totalValue: PIXI.Text; // value

    private running: boolean = false;

    constructor() {
        this.layer = Layers.Prompts;
    }

    public show(): Contract<boolean> {
        return new Contract(resolve => {
            this.onHide.removeAll();
            this.running = true;
            this.inResolve = resolve;

            this.betOptions = betModel.generateBetOptions();
            this.betOptions.getTotalStakeValues();

            Components.betBar.setEnabled(false);

            smartButton.setFlag(SmartButtonFlag.PROMPT, true);
            Layers.BetBar.hide();
            Layers.SmartButton.hide();

            sequence([
                () => Layers.CelebrationBackground.setScene("bonusbuy"),
                () => Layers.Prompts.setScene("bonusbuy", () => this.setUp()),                
            ]).execute();          
        });
    }

    private onLayoutChange = () => {
        setTimeout(() => {
            this.setUp(true);
        }, 10)
    }

    private setUp(instant: boolean = false) {
        this.closeButton = this.layer.getButton("close");
        this.buyButton = this.layer.getButton("buy");
        this.plusButton = this.layer.getButton("plus") || this.layer.getButton("next");
        this.minusButton = this.layer.getButton("minus") || this.layer.getButton("prev");

        this.totalBetText = this.layer.getText("bet");
        this.totalValue = this.layer.getText("value");

        this.setButtons();
        this.updateFields();
    }

    private setEnabled(enabled: boolean) {
        this.closeButton.setEnabled(enabled);
        this.plusButton.setEnabled(enabled);
        this.minusButton.setEnabled(enabled);
        if(enabled) {
            if(this.isBalanceAboveAmount()) {
                this.buyButton.setEnabled(enabled);
            } else {
                this.buyButton.setEnabled(false);
            }
        } else {
            this.buyButton.setEnabled(enabled);
        }
    }

    private isBalanceAboveAmount() {
        const currentBet = this.betOptions.getTotalStake();
        const amount = currentBet * BonusBuyComponent.MULTIPLIER;
        const balance = gameState.getCurrentGame().balance;
        
        return amount <= balance;
    }

    private buy = () => {
        if(this.isBalanceAboveAmount()) {
            this.setEnabled(false);
            this.buyRequest().then(() => {
                //  const record = gameState.getCurrentGame().latestRecord() as BuyRequest;
                const record = gameState.getCurrentGame().latestRecord() as BuyoutRecord;
                if(record && record.freespinsTriggered === true) {
                    Layers.Prompts.defaultScene().then(() => {
                        Layers.CelebrationBackground.defaultScene().execute();
                        this.destroy();                
                        if(this.inResolve) {
                            this.inResolve(true);
                            this.inResolve = null;
                        }
                        stateMachine.skip();
                    });
                }   
            });
        }
    }

    private incrementBet = () => {
        let index = this.betOptions.totalStakeValues.indexOf(this.betOptions.getTotalStake());
        if (index < this.betOptions.totalStakeValues.length + 1) {
            index++;
            const currentStake = this.betOptions.totalStakeValues[index];
            this.betOptions.setTotalStake(currentStake);
            this.updateFields();
            this.updateBet();
        }
        this.setEnabled(true);
    }

    private decrementBet = () => {
        let index = this.betOptions.totalStakeValues.indexOf(this.betOptions.getTotalStake());
        if (index > 0) {
            index--;
            const currentStake = this.betOptions.totalStakeValues[index];
            this.betOptions.setTotalStake(currentStake);
            this.updateFields();
            this.updateBet();
        }
        this.setEnabled(true);
    }

    private updateBet() {
        betModel.setCoinSize(this.betOptions.coinSize, true);
        betModel.setBetPerLine(this.betOptions.stakePerLine, true);

        Components.betBar.setCoinSize(this.betOptions.coinSize);
        Components.betBar.setStake(this.betOptions.stakePerLine);
        Components.betBar.setBetOptions(this.betOptions);
        Components.betMenu.setBetOptions(this.betOptions);

        Components.paytable.refreshBetOptions();
        HarleyComponents.quickHit.refreshBetOptions();
    }

    private close = () => {
        this.destroy();
        sequence([
            () => this.layer.defaultScene(),
            () => Layers.CelebrationBackground.defaultScene()
        ]).then(() => {        
            Components.betBar.setEnabled(true);

            smartButton.setFlag(SmartButtonFlag.PROMPT, false);
            Layers.BetBar.show();
            Layers.SmartButton.show();
            this.inResolve();
        });
    }

    private updateFields() {
        const currentBet = this.betOptions.getTotalStake();
        this.totalBetText.text = currency.format(currentBet);
        this.totalValue.text = currency.format(currentBet * BonusBuyComponent.MULTIPLIER, undefined, undefined, true);
    }

    private onBuy = () => {
        this.buy();
    }

    private setButtons() {        
        this.closeButton.on(ButtonEvent.POINTER_DOWN, this.close);
        this.buyButton.on(ButtonEvent.POINTER_DOWN, this.onBuy);
        this.plusButton.on(ButtonEvent.POINTER_DOWN, this.incrementBet);
        this.minusButton.on(ButtonEvent.POINTER_DOWN, this.decrementBet);
    }

    private destroy() {
        this.closeButton.off(ButtonEvent.POINTER_DOWN, this.close);
        this.buyButton.off(ButtonEvent.POINTER_DOWN, this.onBuy);
        this.plusButton.off(ButtonEvent.POINTER_DOWN, this.incrementBet);
        this.minusButton.off(ButtonEvent.POINTER_DOWN, this.decrementBet);
        deviceInfo.onLayoutChange.remove(this.onLayoutChange);
    }

    private buyRequest(): Contract<void> {
        return new Contract(resolve => {
            const coinLevel = slotDefinition.coinSizes.indexOf(this.betOptions.coinSize) + 1;
            const action: any = {
                type: "BUYOUT",
                bet: { bet_level: this.betOptions.stakePerLine / revolverModel.creditBaseMultiplier, coin_level: coinLevel, lines: this.betOptions.lines }
            };

            const requestPayLoad: any = {
                cmd: "RESULTS",
                additionalData: {},
                session: revolverModel.session,
                action
            };

            try {
                if (revolverModel.getGameCode()) {
                    requestPayLoad.additionalData.gameCode = revolverModel.getGameCode();
                }
                if (revolverModel.getOperator()) {
                    requestPayLoad.additionalData.getOperator = revolverModel.getOperator();
                }
                if (revolverModel.getOperatorId()) {
                    requestPayLoad.additionalData.getOperatorId = revolverModel.getOperatorId();
                }
                if (revolverModel.getBranding()) {
                    requestPayLoad.additionalData.getBranding = revolverModel.getBranding();
                }
                if (window && window.location) {
                    if (window.location.hasOwnProperty("pathname")) {
                        requestPayLoad.additionalData.windowPath = window.location.pathname;
                    }
                }
                if (revolverModel.sendBetConfigInResult) {
                    requestPayLoad.additionalData.betConfig = revolverModel.betConfig;
                }
            } catch (e) {
                // log(e);
            }

            const requestConfig = {
                method: "post",
                url: revolverModel.getBaseURL(),
                withCredentials: true,
                data: JSON.stringify(requestPayLoad)
            } as AxiosRequestConfig;

            if (revolverModel.jwt_token) {
                requestConfig.headers = {
                    "X-RGS-SESSION-TOKEN": revolverModel.jwt_token
                };
            }

            commsManager.requestRaw(requestConfig).then(resolve);
        })
    }
}